import { create } from 'zustand';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/firebase';

/**
 * Draft Batch Store
 *
 * Ephemeral store for agent chat draft mode.
 * Tracks draft items created by agent tool calls per batch (one batch per assistant turn).
 * Allows commit (draft→committed) or discard (delete doc) per item or per batch.
 *
 * No persist middleware — drafts are ephemeral and cleared on page reload.
 */
const useDraftBatchStore = create((set, get) => ({
  // Map of batchId → { items: [{ collection, docId, toolName, summary }], status: 'pending'|'committing'|'committed'|'discarded' }
  batches: {},

  // Per-item status: `${collection}/${docId}` → 'pending'|'committing'|'committed'|'discarded'
  itemStatuses: {},

  /**
   * Add a draft batch or append items to an existing batch
   */
  addDraftBatch: (batchId, items) => {
    set((state) => {
      const existing = state.batches[batchId];
      const newItems = items.map((item) => ({
        collection: item._collection || item.collection,
        docId: item._docId || item.docId,
        toolName: item.toolName || null,
        summary: item.summary || null,
      }));

      const newItemStatuses = { ...state.itemStatuses };
      newItems.forEach((item) => {
        newItemStatuses[`${item.collection}/${item.docId}`] = 'pending';
      });

      return {
        batches: {
          ...state.batches,
          [batchId]: {
            items: existing ? [...existing.items, ...newItems] : newItems,
            status: 'pending',
          },
        },
        itemStatuses: newItemStatuses,
      };
    });
  },

  /**
   * Get a specific draft batch
   */
  getDraftBatch: (batchId) => {
    return get().batches[batchId] || null;
  },

  /**
   * Get total count of pending draft items across all batches
   */
  getBatchCount: () => {
    const { batches } = get();
    return Object.values(batches)
      .filter((b) => b.status === 'pending')
      .reduce((sum, b) => sum + b.items.length, 0);
  },

  /**
   * Get the status of a specific item
   */
  getItemStatus: (collection, docId) => {
    return get().itemStatuses[`${collection}/${docId}`] || null;
  },

  /**
   * Commit all items in a batch (update _status from 'draft' to 'committed')
   */
  commitBatch: async (batchId) => {
    const batch = get().batches[batchId];
    if (!batch || batch.status !== 'pending') return;

    set((state) => ({
      batches: {
        ...state.batches,
        [batchId]: { ...batch, status: 'committing' },
      },
    }));

    try {
      const promises = batch.items.map(async (item) => {
        const key = `${item.collection}/${item.docId}`;
        set((state) => ({
          itemStatuses: { ...state.itemStatuses, [key]: 'committing' },
        }));

        const docRef = doc(db, item.collection, item.docId);
        await updateDoc(docRef, { _status: 'committed' });

        set((state) => ({
          itemStatuses: { ...state.itemStatuses, [key]: 'committed' },
        }));
      });

      await Promise.all(promises);

      set((state) => ({
        batches: {
          ...state.batches,
          [batchId]: { ...state.batches[batchId], status: 'committed' },
        },
      }));
    } catch (err) {
      console.error('[draftBatchStore] commitBatch failed:', err);
      // Revert batch status
      set((state) => ({
        batches: {
          ...state.batches,
          [batchId]: { ...state.batches[batchId], status: 'pending' },
        },
      }));
      throw err;
    }
  },

  /**
   * Discard all items in a batch (delete draft documents)
   */
  discardBatch: async (batchId) => {
    const batch = get().batches[batchId];
    if (!batch || batch.status !== 'pending') return;

    set((state) => ({
      batches: {
        ...state.batches,
        [batchId]: { ...batch, status: 'committing' },
      },
    }));

    try {
      const promises = batch.items.map(async (item) => {
        const key = `${item.collection}/${item.docId}`;
        set((state) => ({
          itemStatuses: { ...state.itemStatuses, [key]: 'committing' },
        }));

        const docRef = doc(db, item.collection, item.docId);
        await deleteDoc(docRef);

        set((state) => ({
          itemStatuses: { ...state.itemStatuses, [key]: 'discarded' },
        }));
      });

      await Promise.all(promises);

      set((state) => ({
        batches: {
          ...state.batches,
          [batchId]: { ...state.batches[batchId], status: 'discarded' },
        },
      }));
    } catch (err) {
      console.error('[draftBatchStore] discardBatch failed:', err);
      set((state) => ({
        batches: {
          ...state.batches,
          [batchId]: { ...state.batches[batchId], status: 'pending' },
        },
      }));
      throw err;
    }
  },

  /**
   * Commit a single item
   */
  commitItem: async (collection, docId) => {
    const key = `${collection}/${docId}`;
    const currentStatus = get().itemStatuses[key];
    if (currentStatus !== 'pending') return;

    set((state) => ({
      itemStatuses: { ...state.itemStatuses, [key]: 'committing' },
    }));

    try {
      const docRef = doc(db, collection, docId);
      await updateDoc(docRef, { _status: 'committed' });

      set((state) => ({
        itemStatuses: { ...state.itemStatuses, [key]: 'committed' },
      }));
    } catch (err) {
      console.error('[draftBatchStore] commitItem failed:', err);
      set((state) => ({
        itemStatuses: { ...state.itemStatuses, [key]: 'pending' },
      }));
      throw err;
    }
  },

  /**
   * Discard a single item (delete draft document)
   */
  discardItem: async (collection, docId) => {
    const key = `${collection}/${docId}`;
    const currentStatus = get().itemStatuses[key];
    if (currentStatus !== 'pending') return;

    set((state) => ({
      itemStatuses: { ...state.itemStatuses, [key]: 'committing' },
    }));

    try {
      const docRef = doc(db, collection, docId);
      await deleteDoc(docRef);

      set((state) => ({
        itemStatuses: { ...state.itemStatuses, [key]: 'discarded' },
      }));
    } catch (err) {
      console.error('[draftBatchStore] discardItem failed:', err);
      set((state) => ({
        itemStatuses: { ...state.itemStatuses, [key]: 'pending' },
      }));
      throw err;
    }
  },

  /**
   * Reset all batches (e.g. when clearing chat)
   */
  reset: () => {
    set({ batches: {}, itemStatuses: {} });
  },
}));

export default useDraftBatchStore;
